import classNames from 'classnames';
import { useState } from 'react';
import type { FC, ReactNode } from 'react';
import { AccordionPanel } from './AccordionPanel';
import './Accordion.css';
import type { TransitionDuration } from '@ipfabric/frontend-shared-assets/tailwind/tailwindConfigTypes';
import { useElementListRef } from 'utils/hooks/useElementListRef';

const ANIMATION_DURATION_COLLAPSE: TransitionDuration = '300';
const ANIMATION_DURATION_SCROLL = 300;

export interface AccordionPanelContent {
  key: string;
  label: ReactNode | string;
  content: ReactNode | string;
  initialOpen?: boolean;
}

export interface AccordionProps {
  className?: string;
  arrowPosition?: 'left' | 'right';
  panels: AccordionPanelContent[];
  /** When user open second panel then first one will auto-collapse, if it's true */
  allowMultipleOpen?: boolean;
  /**
   * It is called after close/open panel intentionally.
   * Duration is animation duration in ms, default is 300ms,
   * but it can be changed by setting animationScrollDuration
   */
  scrollTo?: (offset: number, duration: number) => void;
  /** Have to be provided scrollTo callback */
  animationScrollDuration?: number;
  animationCollapseDuration?: TransitionDuration;
  /** if set true then lightgray-d is used, else lightgray-e */
  backgroundHoverColorDark?: boolean;
}

/**
 * Accordion component
 * Intended for vertically stacked AccordionPanels.
 * Component is intended to unite AccordionPanels behaviour, handle option when only one panel can be opened
 * and handle scrolling to opened panel if scrollTo callback is provided.
 */
export const Accordion: FC<AccordionProps> = (props) => {
  const openedPanelKeys = getOpenedPanelKeys(props.panels);

  const [panelHeaderRefs, setPanelHeaderRefs] = useElementListRef<
    string,
    HTMLDivElement
  >();

  const [expandedItems, setExpandedItems] = useState<string[]>(
    props.allowMultipleOpen
      ? openedPanelKeys
      : keepOnlyLastKey(openedPanelKeys),
  );

  const handleToggle = (key: string) => {
    if (props.allowMultipleOpen) {
      setExpandedItems(
        expandedItems.includes(key)
          ? expandedItems.filter((item) => item !== key)
          : [...expandedItems, key],
      );
    } else {
      setExpandedItems(expandedItems.includes(key) ? [] : [key]);
    }
  };

  return (
    <div className={classNames('ipf-accordion', props.className)}>
      {props.panels.map((panel) => (
        <AccordionPanel
          key={panel.key}
          label={panel.label}
          content={panel.content}
          open={expandedItems.includes(panel.key)}
          arrowPosition={props.arrowPosition}
          animationCollapseDuration={
            props.animationCollapseDuration ?? ANIMATION_DURATION_COLLAPSE
          }
          backgroundHoverColorDark={props.backgroundHoverColorDark}
          setHeaderRefCallback={setPanelHeaderRefs(panel.key)}
          onToggle={() => {
            const previousPanelBottomPosition =
              getBottomPositionOfPreviousPanel(
                panelHeaderRefs,
                props.panels,
                panel.key,
              );
            if (previousPanelBottomPosition !== null) {
              props.scrollTo?.(
                previousPanelBottomPosition,
                props.animationScrollDuration ?? ANIMATION_DURATION_SCROLL,
              );
            }
            handleToggle(panel.key);
          }}
        />
      ))}
    </div>
  );
};

function getBottomPositionOfPreviousPanel(
  panelHeaderRefs: Map<string, HTMLDivElement>,
  panelsOrderedArray: AccordionPanelContent[],
  currentPanelKey: string,
) {
  const previousPanelIndex =
    panelsOrderedArray.findIndex((panel) => panel.key === currentPanelKey) - 1;
  if (previousPanelIndex < 0) {
    return panelHeaderRefs.get(currentPanelKey)?.offsetTop ?? null;
  }
  // Fixed noUncheckedIndexedAccess NIM-19408
  const previousPanelKey = panelsOrderedArray[previousPanelIndex]!.key;
  const previousPanelRef = panelHeaderRefs.get(previousPanelKey);
  if (previousPanelRef) {
    return (
      previousPanelRef.offsetTop +
      previousPanelRef.getBoundingClientRect().height
    );
  }
  // it should never happen
  return null;
}

const getOpenedPanelKeys = (panels: AccordionPanelContent[]): string[] =>
  panels.filter((item) => item.initialOpen).map((item) => item.key);

const keepOnlyLastKey = (keys: string[]) => keys.slice(keys.length - 1);
